import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Mail, Link as LinkIcon, Send, CheckCircle2, XCircle, Copy, Eye } from 'lucide-react';

const STATUS_STYLES = {
  sent: { label: 'Inviato', variant: 'default' },
  submitted: { label: 'Ricevuto', variant: 'default' },
  approved: { label: 'Approvato', variant: 'outline' },
  rejected: { label: 'Rifiutato', variant: 'destructive' },
};

export default function PartnerOnboardingAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState('');
  const [invitedName, setInvitedName] = useState('');
  const [sending, setSending] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listOnboarding();
      setItems(r || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-apri il dialog di dettaglio se arriviamo da una notifica con ?id=xxx
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (!idParam || !items.length) return;
    const found = items.find(it => it.id === idParam);
    if (found) {
      setDetail(found);
    } else {
      toast.info('Candidatura non trovata o già archiviata');
    }
    // pulisco il query param dall'URL per evitare riaperture accidentali
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const sendInvite = async () => {
    if (!invitedEmail.trim()) { toast.error('Inserisci email'); return; }
    setSending(true);
    try {
      const r = await api.createOnboarding({ invited_email: invitedEmail.trim(), invited_name: invitedName.trim() });
      toast.success('Invito onboarding inviato via email');
      try { await navigator.clipboard?.writeText(r.onboarding_link); toast.info('Link copiato negli appunti'); } catch {}
      setInviteOpen(false); setInvitedEmail(''); setInvitedName('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore invio'); }
    finally { setSending(false); }
  };

  const approve = async (it) => {
    if (!window.confirm(`Approvare la candidatura di ${it.submitted_data?.name || it.invited_email}? Verrà creata l'azienda partner.`)) return;
    try {
      const r = await api.approveOnboarding(it.id);
      toast.success(`Azienda creata (id: ${r.company_id.substring(0,8)}). Ora puoi configurarne il profilo completo.`);
      setDetail(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore approvazione'); }
  };
  const reject = async (it) => {
    const reason = window.prompt('Motivo del rifiuto:');
    if (reason === null) return;
    try {
      await api.rejectOnboarding(it.id, reason);
      toast.success('Candidatura rifiutata');
      setDetail(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const resend = async (it) => {
    try { await api.resendOnboarding(it.id); toast.success('Email re-inviata'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  return (
    <div>
      <PageHeader
        title="Onboarding Aziende Partner"
        description="Invia un link personalizzato alle aziende interessate a diventare partner. Loro compileranno il form e caricheranno i documenti; tu approverai e verrà creato automaticamente il profilo azienda."
        actions={<Button onClick={() => setInviteOpen(true)} data-testid="invite-btn"><Send className="size-4 mr-2" />Nuovo invito</Button>}
      />
      <Card>
        <CardContent className="p-0">
          {loading ? <div className="p-10 text-center text-sm">Caricamento…</div> : items.length === 0 ? (
            <div className="p-10 text-center">
              <Mail className="size-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm">Nessun invito inviato. Clicca "Nuovo invito" per iniziare.</div>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Email invitata</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Ragione sociale</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map(it => {
                  const st = STATUS_STYLES[it.status] || { label: it.status, variant: 'secondary' };
                  return (
                    <TableRow key={it.id} data-testid={`onboarding-${it.id}`}>
                      <TableCell className="font-medium text-sm">{it.invited_email}</TableCell>
                      <TableCell className="text-sm">{it.invited_name || '—'}</TableCell>
                      <TableCell className="text-sm">{it.submitted_data?.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{new Date(it.created_at).toLocaleDateString('it-IT')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { try { navigator.clipboard.writeText(it.onboarding_link); toast.success('Link copiato'); } catch { toast.info(it.onboarding_link); } }} title="Copia link" data-testid={`copy-${it.id}`}><Copy className="size-3.5" /></Button>
                          {it.status === 'sent' && <Button size="sm" variant="ghost" onClick={() => resend(it)} title="Re-invia email"><Mail className="size-3.5" /></Button>}
                          {it.status === 'submitted' && <Button size="sm" variant="outline" onClick={() => setDetail(it)} data-testid={`review-${it.id}`}><Eye className="size-3.5 mr-1" />Rivedi</Button>}
                          {it.status === 'approved' && <Badge variant="outline" className="text-[10px]">✓ Company creata</Badge>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog nuovo invito */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo invito onboarding</DialogTitle>
            <DialogDescription>Invia un link personalizzato all'azienda invitata via email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email azienda invitata *</Label>
              <Input type="email" value={invitedEmail} onChange={e => setInvitedEmail(e.target.value)} placeholder="referente@azienda.it" data-testid="invite-email" />
            </div>
            <div>
              <Label className="text-xs">Nome referente (opzionale)</Label>
              <Input value={invitedName} onChange={e => setInvitedName(e.target.value)} placeholder="Mario Rossi" data-testid="invite-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} disabled={sending}>Annulla</Button>
            <Button onClick={sendInvite} disabled={sending} data-testid="invite-send">{sending ? 'Invio…' : 'Invia link via email'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog dettaglio candidatura */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Candidatura: {detail?.submitted_data?.name || detail?.invited_email}</DialogTitle>
            <DialogDescription>Rivedi le risposte del form e i documenti caricati, quindi approva o rifiuta.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(detail.submitted_data || {}).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-xs text-muted-foreground uppercase">{k.replace(/_/g, ' ')}</div>
                    <div className="font-medium break-words">{String(v || '—')}</div>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t">
                <div className="text-xs text-muted-foreground mb-1">Documenti caricati ({(detail.documents || []).length})</div>
                <div className="space-y-1">
                  {(detail.documents || []).map(d => (
                    <a key={d.key} href={d.url} target="_blank" rel="noreferrer" className="block text-xs text-primary hover:underline">
                      📎 {d.label} ({d.file_name || 'file'})
                    </a>
                  ))}
                  {(detail.documents || []).length === 0 && <div className="text-xs text-muted-foreground">Nessun documento</div>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => reject(detail)} data-testid="reject-btn"><XCircle className="size-4 mr-1" />Rifiuta</Button>
            <Button onClick={() => approve(detail)} data-testid="approve-btn"><CheckCircle2 className="size-4 mr-1" />Approva e crea azienda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
